import { Score } from '@/components/InterviewReview/InterviewReview.component';
import { InterviewScore, InterviewScoreEnum, TechnicalValidationCompetency } from '@/types/api';
import { yesOrNoOptions } from './dropdownOptions';

export enum InterviewTypes {
  PRE_SCREENING = 'PRE_SCREENING',
  SCREENING = 'SCREENING',
  TECHNICAL_CHALLENGE = 'TECHNICAL_CHALLENGE',
  TEAM_INTERVIEW = 'TEAM_INTERVIEW',
}

export const defaultInterviewsStatuses = {
  [InterviewTypes.PRE_SCREENING]: 'LANGUAGE_CHECK_SUBMITTED',
  [InterviewTypes.SCREENING]: 'SCREENING_SCHEDULED',
  [InterviewTypes.TEAM_INTERVIEW]: 'TEAM_INTERVIEW_SCHEDULED',
  [InterviewTypes.TECHNICAL_CHALLENGE]: 'TECHNICAL_CHALLENGE_SUBMITTED',
};

export const defaultRatingQuantity = {
  [InterviewTypes.PRE_SCREENING]: 5,
  [InterviewTypes.SCREENING]: 3,
  [InterviewTypes.TEAM_INTERVIEW]: 4,
};

export const interviewsThatShouldBeSent = [
  InterviewTypes.PRE_SCREENING,
  InterviewTypes.TECHNICAL_CHALLENGE,
];

export const interviewsThatHasAssignedReviewers = [
  InterviewTypes.TEAM_INTERVIEW,
  InterviewTypes.TECHNICAL_CHALLENGE,
];

export const interviewsThatHasNotes = [InterviewTypes.SCREENING, InterviewTypes.TEAM_INTERVIEW];

export const PRE_SCREENING_SCORE_INPUTS: InterviewScore[] = [
  {
    scoreType: InterviewScoreEnum.FLUENCY,
    score: 1,
  },
  {
    scoreType: InterviewScoreEnum.SPONTANEITY,
    score: 1,
  },
  {
    scoreType: InterviewScoreEnum.VOCABULARY,
    score: 1,
  },
];

export const SCREENING_SCORE_INPUTS: InterviewScore[] = [
  {
    scoreType: InterviewScoreEnum.EXCELLENCE,
    score: 1,
  },
  {
    scoreType: InterviewScoreEnum.COMMUNICATION,
    score: 1,
  },
  {
    scoreType: InterviewScoreEnum.ENGLISH,
    score: 1,
  },
  {
    scoreType: InterviewScoreEnum.COLLABORATION,
    score: 1,
  },
  {
    scoreType: InterviewScoreEnum.CULTURAL_FIT,
    score: 1,
  },
  {
    scoreType: InterviewScoreEnum.SELF_MANAGEMENT,
    score: 1,
  },
  {
    scoreType: InterviewScoreEnum.YEARS_OF_EXPERIENCE_WITH_REQUESTED_TECHNOLOGY,
    score: 1,
  },
];

export const SCREENING_SCORE_WEIGHTS = {
  [InterviewScoreEnum.COLLABORATION]: 8,
  [InterviewScoreEnum.SELF_MANAGEMENT]: 8,
  [InterviewScoreEnum.COMMUNICATION]: 10,
  [InterviewScoreEnum.EXCELLENCE]: 9,
  [InterviewScoreEnum.CULTURAL_FIT]: 10,
  [InterviewScoreEnum.YEARS_OF_EXPERIENCE_WITH_REQUESTED_TECHNOLOGY]: 6,
  [InterviewScoreEnum.ENGLISH]: 9,
};

export const TEAM_INTERVIEW_SCORE_WEIGHTS = {
  [InterviewScoreEnum.COMMUNICATION]: 9,
  [InterviewScoreEnum.TECHNICAL_KNOWLEDGE_AND_PROBLEM_SOLVING]: 8,
  [InterviewScoreEnum.AWARENESS]: 4,
  [InterviewScoreEnum.COLLABORATION]: 6,
  [InterviewScoreEnum.CURIOSITY]: 9,
};

const VALUE_TO_NORMALIZE_WEIGHTED_INPUT_RANGE = 2;

export const getWeightedScore = (
  currentValue: number,
  interviewType: InterviewTypes,
  interviewScore: InterviewScoreEnum
): number => {
  if (interviewType === InterviewTypes.SCREENING) {
    const relativeInputValue = currentValue + VALUE_TO_NORMALIZE_WEIGHTED_INPUT_RANGE;
    return relativeInputValue * (SCREENING_SCORE_WEIGHTS[interviewScore] || 1);
  }

  if (interviewType === InterviewTypes.TEAM_INTERVIEW) {
    return currentValue * (TEAM_INTERVIEW_SCORE_WEIGHTS[interviewScore] || 1);
  }

  return currentValue;
};

export const getNormalizedScore = (
  id = '',
  currentValue: number,
  interviewType: InterviewTypes,
  interviewScore: InterviewScoreEnum
): string => {
  if (id && interviewType === InterviewTypes.SCREENING) {
    const valueNotWeighted = currentValue / (SCREENING_SCORE_WEIGHTS[interviewScore] || 1);
    return (valueNotWeighted - VALUE_TO_NORMALIZE_WEIGHTED_INPUT_RANGE).toString();
  }

  if (id && interviewType === InterviewTypes.TEAM_INTERVIEW) {
    const valueNotWeighted = currentValue / (TEAM_INTERVIEW_SCORE_WEIGHTS[interviewScore] || 1);
    return valueNotWeighted.toString();
  }

  return currentValue.toString();
};

const ONE_HUNDRED_PERCENT = 100;
const MAX_WEIGHTED_SCREENING_POINTS = 300;
const MAX_WEIGHTED_TEAM_INTERVIEW_POINTS = 144;

const getMaxWeightedPoints = (interviewType: InterviewTypes): number =>
  interviewType === InterviewTypes.SCREENING
    ? MAX_WEIGHTED_SCREENING_POINTS
    : MAX_WEIGHTED_TEAM_INTERVIEW_POINTS;

export const getWeightedCurrentScore = (interviewType: InterviewTypes, scores: Score[]): number => {
  if (!interviewsThatHasNotes.includes(interviewType)) {
    const totalScore = scores.reduce(
      (accumulator: number, inputScore) => accumulator + Number(inputScore.score),
      0
    );

    return Math.round(totalScore);
  }

  const weightedTotalScore = scores.reduce(
    (accumulator: number, inputScore) =>
      accumulator +
      getWeightedScore(
        Number(inputScore.score),
        interviewType,
        InterviewScoreEnum[inputScore.type]
      ),
    0
  );

  const percentageWeightedScore =
    (weightedTotalScore * ONE_HUNDRED_PERCENT) / getMaxWeightedPoints(interviewType);

  return Math.round(percentageWeightedScore);
};

export const numberedTechnicalScores = [
  InterviewScoreEnum.TECHNICAL_VALIDATION_FOLLOWS_CURRENT_STRUCTURE,
  InterviewScoreEnum.TECHNICAL_VALIDATION_TECHNICAL_DESIGN_QUALITY,
];

export const getRangeOptions = (maximumNote = 3): string[] => {
  const parsedMaximumNote = Math.floor(maximumNote);
  return Array.from({ length: parsedMaximumNote + 1 }, (_, i) => (0 + i).toString());
};

export const getInputNumberedValue = (value: string, maxScoreValue?: number): number => {
  let numberedValue = Number(value) || 0;

  if (value === 'Yes') {
    numberedValue = maxScoreValue || 1;
  }

  if (value === 'No') {
    numberedValue = 0;
  }

  return numberedValue;
};

export const TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER = 'Choose an option...';

export const TECHNICAL_VALIDATION_COMPETENCIES: TechnicalValidationCompetency[] = [
  {
    name: 'Best Practices',
    additionalInfo: '',
    weight: 3,
    scores: [
      {
        name: InterviewScoreEnum.TECHNICAL_VALIDATION_CONTAINS_ERROR,
        label: 'The new feature contains error handling to avoid breaking the App',
        value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
        options: yesOrNoOptions,
      },
      {
        name: InterviewScoreEnum.TECHNICAL_VALIDATION_FOLLOWS_BEST_PRACTICES,
        label:
          'The new feature follows the best practices of the given library/framework and uses the tools provided by them. When reading the code it doesn’t feel like it was written by someone coming from another language/framework.',
        value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
        options: yesOrNoOptions,
      },
    ],
  },
  {
    name: 'Code Organization',
    additionalInfo: '',
    weight: 1,
    scores: [
      {
        name: InterviewScoreEnum.TECHNICAL_VALIDATION_FOLLOWS_CURRENT_STRUCTURE,
        label: 'Solution follows the current project structure',
        value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
        options: getRangeOptions(),
      },
    ],
  },
  {
    name: 'Code Legibility',
    additionalInfo: '',
    weight: 1,
    scores: [
      {
        name: InterviewScoreEnum.TECHNICAL_VALIDATION_MEANINGFUL_NAMES,
        label: 'Meaningful names on variables, methods, and classes',
        value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
        options: yesOrNoOptions,
      },
      {
        name: InterviewScoreEnum.TECHNICAL_VALIDATION_NAMED_CONDITIONS,
        label: 'Named conditions and magical numbers',
        value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
        options: yesOrNoOptions,
      },
      {
        name: InterviewScoreEnum.TECHNICAL_VALIDATION_BOOLEAN_SUFFIXES,
        label: 'Boolean suffixes',
        value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
        options: yesOrNoOptions,
      },
      {
        name: InterviewScoreEnum.TECHNICAL_VALIDATION_SMALL_FUNCTIONS,
        label: 'Methods and functions are specific, small, and concise',
        value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
        options: yesOrNoOptions,
      },
      {
        name: InterviewScoreEnum.TECHNICAL_VALIDATION_ONE_LEVEL_INDENTATION,
        label: 'Only one level of indentation per item',
        value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
        options: yesOrNoOptions,
      },
      {
        name: InterviewScoreEnum.TECHNICAL_VALIDATION_CONSISTENT_CODEBASE,
        label: 'Consistency throughout the codebase',
        value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
        options: yesOrNoOptions,
      },
      {
        name: InterviewScoreEnum.TECHNICAL_VALIDATION_WELL_SEPARATED_CODE,
        label: 'Code is well separated by its concerns',
        value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
        options: yesOrNoOptions,
      },
      {
        name: InterviewScoreEnum.TECHNICAL_VALIDATION_NOT_CONTAIN_REPETITION,
        label: 'Code does not contain repeated code',
        value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
        options: yesOrNoOptions,
      },
      {
        name: InterviewScoreEnum.TECHNICAL_VALIDATION_SAME_CODE_STYLE,
        label: 'Code follows the same code style',
        value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
        options: yesOrNoOptions,
      },
    ],
  },
  {
    name: 'Tests',
    additionalInfo: '',
    weight: 1,
    scores: [
      {
        name: InterviewScoreEnum.TECHNICAL_VALIDATION_CONTAINS_UNIT_TESTS,
        label: 'Project contains Unit Tests',
        value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
        options: yesOrNoOptions,
      },
      {
        name: InterviewScoreEnum.TECHNICAL_VALIDATION_CONTAINS_INTEGRATION_TESTS,
        label: 'Project contains Integration Tests or e2e Tests',
        value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
        options: yesOrNoOptions,
      },
      {
        name: InterviewScoreEnum.TECHNICAL_VALIDATION_COVERAGE_FIFTY_SEVENTY,
        label: 'New feature coverage 50-70%',
        value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
        options: yesOrNoOptions,
      },
      {
        name: InterviewScoreEnum.TECHNICAL_VALIDATION_COVERAGE_SEVENTY_ONE_HUNDRED,
        label: 'New feature coverage 71-100%',
        value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
        options: yesOrNoOptions,
      },
    ],
  },
  {
    name: 'Technical Design',
    additionalInfo: '',
    weight: 0.5,
    scores: [
      {
        name: InterviewScoreEnum.TECHNICAL_VALIDATION_TECHNICAL_DESIGN_QUALITY,
        label: 'Quality of technical design',
        value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
        options: getRangeOptions(4),
      },
    ],
  },
  {
    name: 'Git',
    additionalInfo: '',
    weight: 0.5,
    scores: [
      {
        name: InterviewScoreEnum.TECHNICAL_VALIDATION_WELL_SEPARATED_COMMITS,
        label: 'Commits are well separated',
        value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
        options: yesOrNoOptions,
      },
      {
        name: InterviewScoreEnum.TECHNICAL_VALIDATION_CLEAR_COMMITS,
        label:
          'Commit messages are clear and descriptive, using any standard such as conventional commits is a plus',
        value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
        options: yesOrNoOptions,
      },
    ],
  },
];

export const TEAM_INTERVIEW_SCORE_INPUTS: InterviewScore[] = [
  {
    scoreType: InterviewScoreEnum.COMMUNICATION,
    score: 1,
  },

  {
    scoreType: InterviewScoreEnum.AWARENESS,
    score: 1,
  },
  {
    scoreType: InterviewScoreEnum.COLLABORATION,
    score: 1,
  },
  {
    scoreType: InterviewScoreEnum.TECHNICAL_KNOWLEDGE_AND_PROBLEM_SOLVING,
    score: 1,
  },
  {
    scoreType: InterviewScoreEnum.CURIOSITY,
    score: 1,
  },
];
